// Public snapshot payload for `auction:{auctionId}:snapshot` — the
// per-auction realtime channel introduced in ADR 011 §2. Carries the
// auction's realtime-relevant state on every event so the
// SnapshotContext can replace state wholesale.
//
// ADR023 (BL-645): the projection carries the auction's decision-stamp
// INPUTS, never the derived `status` label. Clients re-derive a coarse
// auction phase via `deriveAuctionPhaseCoarse` (snapshot-only inputs) on
// their per-second tick, so a snapshot can no longer go stale just
// because the clock advanced (ADR023 Decision 4).
//
// PUBLIC-SAFE only. (BUG-1299 added `lotExtensionSeconds` under that bar: the
// anti-snipe extension is not sensitive and buyers already experience its
// effect when their own bid extends a lot.) The catch-up endpoint for this topic is gated on
// "auction exists" only; the projected fields (pause timestamp, increment
// table, scheduling + lifecycle decision stamps) are the same public data
// the unauthenticated auction-detail page renders. No bidder PII, no
// payment internals, no operator-only attributes.

/**
 * Full snapshot — the auction's realtime-relevant state at a point in
 * time. Mirrors the typed columns on the `auction_snapshot` table
 * (migrations `20260608215513_create_auction_snapshot.sql` +
 * `20260630200702_narrow_snapshot_projections_to_decision_stamps_bl645.sql`).
 *
 * - `pausedAt` — null when not paused; drives the "bidding paused by clerk"
 *   overlay.
 * - `bidIncrements` — the tiered increment table; drives the next-bid floor.
 *   `null` is legitimate (fall back to tenant default increments).
 * BL-1568 removed `scheduledCloseAt`. It projected `auctions.first_lot_close_at`
 * onto this topic and had ZERO readers monorepo-wide — a dead second copy of a
 * close time sitting beside the live one, which is the drift shape PRJ-63 exists
 * to close (ADR 011 — Durable Events: delete outright, add nothing back).
 *
 * - The decision stamps + scheduling (`auctionType` … `closingType`) — the
 *   inputs `deriveAuctionPhaseCoarse` reads to derive the auction phase.
 *   `auctionType` is carried as text (the predicate string-compares the live
 *   guard). `startDate` is `opensAt` for the `ready` phase / countdown.
 */
/**
 * One ring's position in its auction's canonical order (BL-1742).
 *
 * TWO FIELDS, AND THAT IS A RULE RATHER THAN A CONVENIENCE. `auction_rings` also
 * carries `buyers_premium`, `first_lot_close_at` and `starting_lot_number`.
 * NONE of them may be added here. BP in particular is compliant-by-omission
 * across the whole codebase precisely because it rides no snapshot projection
 * (ADR 032 — Fee Cascade § 9, and `architecture.md` -> "Compliant by
 * construction — buyer's premium"); projecting it opens a second live delivery
 * path for a value every subscribing surface already renders over REST.
 *
 * THIS IS THE CLIENT-SAFE VIEW, and there is a DB-layer twin:
 * `RingIndexEntry` in `@auctogon/db/types/jsonb/ring-index`, which is what the
 * generated schema stamps as `jsonb("ring_index").$type<RingIndexEntry[]>()`.
 *
 * Two declarations of two fields is not an oversight. That module is
 * `server-only` (as every sibling in that folder is) and this shape has to reach
 * the clerk console's pure band derivation in the browser; `packages/db` also
 * cannot import `@auctogon/core*`, since core depends on db and not the reverse.
 * So no single declaration can serve both layers. It is the same split
 * `BidIncrementRange` already lives with.
 *
 * Keep the two in sync — they are two views of ONE column, and adding a field to
 * one without the other will not fail a build, only drift.
 */
export interface RingIndexEntry {
    /** `auction_rings.id`, the join key against a lot's live `ringId`. */
    ringId: string;
    /**
     * 1-based position in the auction's ring order, ranked server-side over
     * `sort_order ASC NULLS FIRST, created_at ASC` — the same clause the admin
     * ring reads and the bidder's ring read use, so staff and buyers never see a
     * different "Ring 2" for one sale.
     */
    position: number;
}

export interface AuctionSnapshot {
    pausedAt: string | null;
    bidIncrements: unknown | null;
    // ── ADR023 (BL-645) decision-stamp + scheduling inputs ──
    auctionType: string | null;
    startDate: string | null;
    readiedAt: string | null;
    openedAt: string | null;
    closedAt: string | null;
    deletedAt: string | null;
    /**
     * BL-1291 — `auctions.pre_bidding_at`, the auction half of the two-stamp
     * pre-bidding window. Rides the snapshot for the same reason `openedAt`
     * does: the client's `deriveLotPhase` needs it as an input, and it is what
     * makes "pre-bidding is open" a SINGLE-source derivation. Before this field
     * the client mixed a live snapshot phase with a frozen SSR
     * `hasPreBiddingLots` flag, so closing pre-bidding left the bidder reading
     * "pre-bidding" until a page reload (rule #32 — AC 5).
     *
     * Public-safe: the same "this auction is taking pre-bids" fact the
     * unauthenticated catalog already renders.
     */
    preBiddingAt: string | null;
    /**
     * BUG-909: static per-auction config (`'manual' | 'automatic'`), not a
     * decision stamp — but the client-side `deriveLotPhase` predicate needs it
     * as an input to split the awaiting-resolution rung into `awaiting-
     * resolution` (auto-close, closed) vs `last-call` (manual-close, still
     * biddable). Rides the snapshot for the same reason `type`/`openedAt` do:
     * the phase re-derives live on every clock tick (BUG-887), so the input
     * has to be available client-side, not just server-side.
     */
    closingType: string | null;
    /**
     * BUG-1299: the anti-snipe extension, in seconds. Static per-auction config
     * like {@link AuctionSnapshot.closingType} — it never changes tick-to-tick.
     * Rides the snapshot because the clerk console is a MULTI-OPERATOR surface:
     * one clerk changing the extension has to reach the other's console, and
     * this topic is the only live path between them.
     *
     * `null` means "no snapshot has landed yet" (see {@link EMPTY_AUCTION_SNAPSHOT}),
     * NOT "no extension" — 0 is the real value for anti-snipe off. Consumers must
     * keep the two apart rather than collapsing both to a default.
     */
    lotExtensionSeconds: number | null;
    /**
     * BL-2592 — how many seconds before a lot's close the countdown starts
     * flashing. Static per-auction config like {@link AuctionSnapshot.closingType}
     * and {@link AuctionSnapshot.lotExtensionSeconds} — the auction's Bidding
     * settings can change it live, and this topic is the only live path to
     * every open tab watching the lot (ADR 011 — Durable Events).
     *
     * `null` means "no snapshot has landed yet", NOT "flash off" — 0 is the
     * real value for that. Same unknown-vs-zero discipline as
     * {@link AuctionSnapshot.lotExtensionSeconds}.
     */
    flashWindowSeconds: number | null;
    /**
     * BL-1742 — the auction's ranked rings, the clerk console's ONLY source for
     * a ring ordinal.
     *
     * `null` means "no snapshot has landed yet" (same unknown-vs-empty
     * discipline as {@link AuctionSnapshot.lotExtensionSeconds}); an auction
     * that genuinely has no rings projects `[]`. A consumer that collapses the
     * two renders "No ring" for every lot during the pre-catch-up frame.
     *
     * Refreshed by a `DerivedSnapshotDependency { child: 'auction_ring', parent:
     * 'auction' }` — `auction_rings` is FAN-IN on this topic (many rings → one
     * auction), which is what makes the invalidation expressible at all (rule
     * #40). A per-LOT ring position is fan-out and stays unprojectable.
     */
    ringIndex: RingIndexEntry[] | null;
}

/**
 * Input row shape — duck-typed to match the Drizzle select the publisher
 * uses when reading the source `auctions` row. Listing only the fields we
 * read keeps callers free to project wider rows without breaking the mapper.
 */
export interface AuctionSnapshotRow {
    pausedAt: string | null;
    bidIncrements: unknown | null;
    // ── ADR023 (BL-645) decision-stamp + scheduling inputs from `auctions` ──
    auctionType: string | null;
    startDate: string | null;
    readiedAt: string | null;
    openedAt: string | null;
    closedAt: string | null;
    deletedAt: string | null;
    /** BL-1291: `auctions.pre_bidding_at` — see {@link AuctionSnapshot.preBiddingAt}. */
    preBiddingAt: string | null;
    /** BUG-909: `auctions.closing_type` — see {@link AuctionSnapshot.closingType}. */
    closingType: string | null;
    /** BUG-1299: `auctions.lot_extension_seconds` — see {@link AuctionSnapshot.lotExtensionSeconds}. */
    lotExtensionSeconds: number | null;
    /** BL-2592: `auctions.flash_window_seconds` — see {@link AuctionSnapshot.flashWindowSeconds}. */
    flashWindowSeconds: number | null;
    /**
     * BL-1742: the ranked ring list. NOT a column on `auctions` — the publisher
     * reads it from `auction_rings` as a second query, which is why this row
     * shape is no longer a 1:1 mirror of one source row.
     */
    ringIndex: RingIndexEntry[] | null;
}

/**
 * Map an auction source row to the public snapshot shape. Pure projection —
 * no lifecycle derivation (the client derives phase via
 * `deriveAuctionPhaseCoarse` over the stamp inputs this carries).
 */
export function mapAuctionSnapshot(row: AuctionSnapshotRow): AuctionSnapshot {
    return {
        pausedAt: row.pausedAt,
        bidIncrements: row.bidIncrements,
        auctionType: row.auctionType,
        startDate: row.startDate,
        readiedAt: row.readiedAt,
        openedAt: row.openedAt,
        closedAt: row.closedAt,
        deletedAt: row.deletedAt,
        preBiddingAt: row.preBiddingAt,
        closingType: row.closingType,
        lotExtensionSeconds: row.lotExtensionSeconds,
        flashWindowSeconds: row.flashWindowSeconds,
        ringIndex: row.ringIndex,
    };
}

/**
 * Default snapshot returned by the catch-up endpoint when no row exists
 * for the requested auction. Mirrors a freshly-created auction's projection
 * so the consumer's typed read doesn't crash on optional chaining. All
 * decision stamps `null` (a draft auction — `deriveAuctionPhaseCoarse`
 * reads `readiedAt == null` → `draft`).
 */
export const EMPTY_AUCTION_SNAPSHOT: AuctionSnapshot = {
    pausedAt: null,
    bidIncrements: null,
    auctionType: null,
    startDate: null,
    readiedAt: null,
    openedAt: null,
    closedAt: null,
    deletedAt: null,
    preBiddingAt: null,
    closingType: null,
    // NOT 0 and NOT the source column's DEFAULT 60: before catch-up lands the
    // console must be able to tell "unknown" from "anti-snipe off" (BUG-1299).
    lotExtensionSeconds: null,
    // BL-2592: same unknown-vs-zero discipline as `lotExtensionSeconds` above.
    flashWindowSeconds: null,
    // NOT `[]`: before catch-up lands, "unknown" and "this auction has no rings"
    // must stay distinguishable, or every lot renders under "No ring" on first
    // paint (BL-1742).
    ringIndex: null,
};
